import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  Trash2,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_OPTIONS = [
  { value: 'Juste', label: 'Juste', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'À traiter', label: 'À traiter', color: 'bg-red-100 text-red-700' },
  { value: 'En cours', label: 'En cours', color: 'bg-amber-100 text-amber-700' }
];

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }
  return years;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const StatWidget = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
    <div className={`p-2 rounded-lg ${color}`}>
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  </div>
);

const ChecklistBilan = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
  const [checklist, setChecklist] = useState(null);
  const [stats, setStats] = useState({ juste: 0, a_traiter: 0, en_cours: 0, total: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClientId && selectedYear) {
      fetchChecklist();
    }
  }, [selectedClientId, selectedYear]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
      if (response.data.length > 0) {
        setSelectedClientId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async () => {
    try {
      const [checklistRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/checklists/${selectedClientId}/${selectedYear}`),
        axios.get(`${API_URL}/checklists/${selectedClientId}/${selectedYear}/stats`)
      ]);
      setChecklist(checklistRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la checklist');
    }
  };

  const handleItemUpdate = async (itemId, field, value) => {
    try {
      const updateData = { [field]: value };
      await axios.put(`${API_URL}/checklists/${selectedClientId}/${selectedYear}/item/${itemId}`, updateData);
      fetchChecklist();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) return;
    try {
      await axios.post(`${API_URL}/checklists/${selectedClientId}/${selectedYear}/item?name=${encodeURIComponent(newItemName)}`);
      toast.success('Poste ajouté');
      setNewItemName('');
      setShowAddItem(false);
      fetchChecklist();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/checklists/${selectedClientId}/${selectedYear}/item/${itemId}`);
      toast.success('Poste supprimé');
      fetchChecklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getClientName = () => {
    const client = clients.find(c => c.id === selectedClientId);
    return client?.company_name || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checklist-bilan-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Checklist Bilan</h1>
      </div>

      {/* Client & Year Selection */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex gap-4 flex-wrap items-end">
          <div className="flex-1 min-w-[250px] space-y-2">
            <Label>Client</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger data-testid="select-client">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32 space-y-2">
            <Label>Année du bilan</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger data-testid="select-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {selectedClientId && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-lg font-semibold">
              Bilan {selectedYear} — {getClientName()}
            </p>
          </div>
        )}
      </div>

      {selectedClientId && checklist && (
        <>
          {/* Stats */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatWidget 
                icon={CheckCircle}
                label="Juste"
                value={stats.juste}
                color="bg-emerald-50 text-emerald-600"
              />
              <StatWidget 
                icon={AlertTriangle}
                label="À traiter"
                value={stats.a_traiter}
                color="bg-red-50 text-red-600"
              />
              <StatWidget 
                icon={Clock}
                label="En cours"
                value={stats.en_cours}
                color="bg-amber-50 text-amber-600"
              />
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                  <ClipboardCheck size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-xl font-bold tabular-nums">{stats.percentage}%</p>
                  <p className="text-xs text-slate-500">Progression</p>
                </div>
              </div>
            </div>
            
            <Progress value={stats.percentage} className="h-2" />
          </div>

          {/* Checklist Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold">Postes à vérifier</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAddItem(true)}
                className="rounded-lg"
                data-testid="add-item-button"
              >
                <Plus size={16} className="mr-1" />
                Ajouter un poste
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="checklist-table">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Statut</th>
                    <th>Commentaire</th>
                    <th>Vérifié par</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checklist.items?.map((item) => (
                    <tr key={item.id} data-testid={`checklist-item-${item.id}`}>
                      <td className="font-medium">
                        {item.name}
                        {!item.is_default && (
                          <Badge variant="outline" className="ml-2 text-xs">Personnalisé</Badge>
                        )}
                      </td>
                      <td>
                        <Select 
                          value={item.status} 
                          onValueChange={(v) => handleItemUpdate(item.id, 'status', v)}
                        >
                          <SelectTrigger className="w-32 h-9" data-testid={`status-select-${item.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <Badge className={opt.color}>{opt.label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={item.comment || ''}
                          onChange={(e) => {
                            // Update local state
                            setChecklist(prev => ({
                              ...prev,
                              items: prev.items.map(i => 
                                i.id === item.id ? { ...i, comment: e.target.value } : i
                              )
                            }));
                          }}
                          onBlur={(e) => handleItemUpdate(item.id, 'comment', e.target.value)}
                          placeholder="Ajouter un commentaire..."
                          className="h-9 text-sm"
                          data-testid={`comment-input-${item.id}`}
                        />
                      </td>
                      <td className="text-sm text-slate-500">
                        {item.verified_by_name || '-'}
                      </td>
                      <td className="text-sm text-slate-500">
                        {item.verified_at ? formatDateTime(item.verified_at) : '-'}
                      </td>
                      <td>
                        {!item.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(item.id)}
                            className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-md" data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un poste</DialogTitle>
            <DialogDescription>
              Ajoutez un poste personnalisé à la checklist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du poste</Label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Ex: Compte courant associé"
                data-testid="new-item-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)} className="rounded-lg">
              Annuler
            </Button>
            <Button onClick={handleAddItem} className="rounded-lg" data-testid="confirm-add-item">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistBilan;
